import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "../services/api";

/**
 * @typedef {Object} Role
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {Role[]} roles
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} AuthState
 * @property {boolean} isAuthenticated
 * @property {User|null} user
 * @property {string|null} token
 * @property {string|null} refreshToken
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {Function} login
 * @property {Function} logout
 * @property {Function} refresh
 * @property {Function} setLoading
 * @property {Function} setError
 * @property {Function} clearError
 */

export const useAuthStore = create(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: null,

      setUser: (userData) => {
        set({
          user: userData,
          isAuthenticated: !!userData,
        });
      },

      refresh: async () => {
        try {
          const response = await fetch("/api/auth/refresh", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.error_description || data.error || "Token refresh failed"
            );
          }

          return { success: true };
        } catch (error) {
          set({
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },

      canManageUsers: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return roleNames.includes("superadmin") || roleNames.includes("admin");
      },

      canManageContent: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return ["superadmin", "admin", "editor"].some((r) =>
          roleNames.includes(r)
        );
      },

      isSuperAdmin: () => {
        const { user } = get();
        if (!user) return false;
        return user.roles?.some((r) => r.name === "superadmin");
      },

      isAdmin: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return roleNames.includes("superadmin") || roleNames.includes("admin");
      },

      logout: async () => {
        try {
          await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          });
        } catch (error) {}

        set({
          isAuthenticated: false,
          user: null,
          error: null,
        });
        localStorage.removeItem("auth-storage");
      },

      refreshUser: async () => {
        try {
          const response = await api.get("/auth/me");
          if (response.data.success) {
            set({ user: response.data.data });
          }
        } catch (error) {}
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);
